import { useState, useEffect } from 'react';

/**
 * Custom hook to get and track cart count from localStorage
 * Handles both old cart format and multiShopCart format (nested and flat)
 */
export function useCartCount(pollingInterval: number = 500) {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cartData = localStorage.getItem('cart');
        const multiShopCart = localStorage.getItem('multiShopCart');
        let count = 0;
        
        // Count from old cart format
        if (cartData) {
          try {
            const cart = JSON.parse(cartData);
            if (Array.isArray(cart)) {
              count += cart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
            }
          } catch (e) {
            console.warn('useCartCount: Error parsing old cart format:', e);
          }
        }
        
        // Count from multiShopCart format
        if (multiShopCart) {
          try {
            const multiCart = JSON.parse(multiShopCart);
            if (Array.isArray(multiCart) && multiCart.length > 0) {
              // Check if it's the nested format: { shop, items: [{ product, quantity }] }
              if (multiCart[0]?.shop && multiCart[0]?.items && Array.isArray(multiCart[0].items)) {
                // Nested structure: array of shop carts
                count += multiCart.reduce((sum: number, shopCart: any) => {
                  if (shopCart.items && Array.isArray(shopCart.items)) {
                    return sum + shopCart.items.reduce((itemSum: number, item: any) => itemSum + (item.quantity || 1), 0);
                  }
                  return sum;
                }, 0);
              } else if (multiCart[0]?.product || multiCart[0]?.quantity !== undefined) {
                // Flat structure: array of { product, shop, quantity }
                count += multiCart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
              }
            }
          } catch (e) {
            console.warn('useCartCount: Error parsing multiShopCart:', e);
          }
        }
        
        setCartCount(prevCount => {
          if (prevCount !== count) {
            console.log('🛒 useCartCount: Cart count updated from', prevCount, 'to', count);
            return count;
          }
          return prevCount;
        });
      } catch (error) {
        console.error('❌ useCartCount: Error reading cart:', error);
        setCartCount(0);
      }
    };

    // Initial update
    updateCartCount();
    
    // Poll for updates
    const interval = setInterval(updateCartCount, pollingInterval);
    
    // Listen for cart update events
    const handleCartUpdate = (event: Event) => {
      console.log('🛒 useCartCount: cartUpdated event received', event);
      // Small delay to ensure localStorage is written
      setTimeout(() => {
        updateCartCount();
      }, 50);
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [pollingInterval]);

  return cartCount;
}
